import { SuggestionOption } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/types';
import { useShouldShowEQLFieldTooltipInQB } from '@modules/common/hooks';
import {
  ChevronRightRounded,
  KeyboardReturnRounded,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  BoxProps,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { EqlFieldValueType } from 'generated/graphql';
import { ReactNode } from 'react';
import { CustomFilterInfoTooltipContent } from '../../CustomFilterInfoTooltipContent';
import { FieldDetailsTooltipContent } from '../../FieldDetailsTooltipContent';
import { FieldIcon } from '../../FieldIcon';

interface SuggestionProps {
  props: BoxProps;
  option: SuggestionOption;
  renderSecondaryAction?: (option: SuggestionOption) => ReactNode;
}

export function Suggestion({
  props,
  option,
  renderSecondaryAction,
}: SuggestionProps) {
  const field = option.field.field;
  const isMoreOption = option.id.endsWith('_more');
  const shouldShowFieldDetailsTooltip = useShouldShowEQLFieldTooltipInQB();

  const getLabel = () => {
    if (isMoreOption) {
      return (
        <Box component="span" sx={{ fontWeight: 400 }}>
          More...
        </Box>
      );
    }

    if (option.value?.value) {
      return (
        <Tooltip
          title={
            shouldShowFieldDetailsTooltip(option.field) ? (
              <FieldDetailsTooltipContent field={option.field} />
            ) : null
          }
          arrow
          placement="right"
        >
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 400 }}>
              {option.field.displayName} is:{' '}
            </Box>
            <Box component="span" sx={{ fontWeight: 500 }}>
              {option.value.displayName}
            </Box>
          </Box>
        </Tooltip>
      );
    }

    if (option.field.valueType === EqlFieldValueType.None) {
      return (
        <Tooltip
          title={
            <CustomFilterInfoTooltipContent
              filterID={option.field.detail?.reference?.referenceID}
              filterName={option.field.displayName}
            />
          }
          arrow
          placement="right"
        >
          <Box
            component="span"
            sx={{ fontWeight: 400 }}
            display="flex"
            alignItems="center"
          >
            <Typography variant="subtitle2">
              {option.field.displayName}
            </Typography>
            <Typography
              variant="caption"
              fontWeight="normal"
              color="text.disabled"
              paddingLeft={1}
            >
              {option.field.detail?.reference?.creatorName}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    if (field) {
      const shouldShowTooltip = shouldShowFieldDetailsTooltip(option.field);

      return (
        <Tooltip
          title={
            shouldShowTooltip ? (
              <FieldDetailsTooltipContent field={option.field} />
            ) : null
          }
          placement="right"
          arrow
        >
          <Box component="span" sx={{ fontWeight: 400 }}>
            {option.field.displayName} is...
          </Box>
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <Box
      display="flex"
      {...props}
      onMouseDown={isMoreOption ? (event) => event.preventDefault() : undefined}
      sx={{
        ...props.sx,
        flexDirection: 'row',
        justifyContent: 'space-between',
        '&.Mui-focused': {
          '.KeyboardIconContainer': {
            opacity: 1,
          },
          '.EditIconContainer': {
            opacity: 1,
          },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ maxWidth: 'calc(100% - 40px)', minWidth: 0 }}
      >
        {isMoreOption ? (
          <ChevronRightRounded fontSize="small" color="primary" />
        ) : (
          <FieldIcon
            field={field}
            iconURL={option.value?.iconURL ?? option.field?.iconURL}
            value={option.value?.value}
            valueType={option.field?.valueType}
            objectType={option.field?.objectType}
          />
        )}
        <Typography
          variant="subtitle2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getLabel()}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} px={1}>
        {renderSecondaryAction && renderSecondaryAction(option)}
        <Box
          component="span"
          bgcolor={(theme) => alpha(theme.palette.primary.main, 0.16)}
          className="KeyboardIconContainer"
          px={1}
          borderRadius={1}
          sx={{ opacity: 0 }}
        >
          <KeyboardReturnRounded
            fontSize="small"
            sx={{ color: (theme) => theme.palette.primary.main }}
          />
        </Box>
      </Stack>
    </Box>
  );
}
